import React, { useContext } from 'react'
import { bool, func, shape, string } from 'prop-types'

// components
import { BarGraph, Grid, Icon, Link, Loader, Select } from 'components/atoms'
import * as Styled from './DesktopCompare.styles'
import {
  ACCORDION_TITLES,
  DetailContentFactory,
} from 'containers/Detail/partials'
import {
  ComparedDataRow,
  NoCompareValue,
} from 'components/molecules/GeneralInformationCompare/partials'
import {
  Accordion,
  GeneralInformationCompare,
  MessageBox,
  QsrIndicator,
} from 'components/molecules'

// helpers && libs
import { entryPointId, mappedTreatmentScores } from 'settings/app/constants'
import { ErrorBoundary, TextContext } from 'hocs'
import { useSelector } from 'react-redux'
import { MobileSwitchProvider, useMobileSwitch } from 'hooks/MobileSwitch'
import { getReplacedString } from 'settings/app/helpers'
import {
  RESULT_HAS_COMPARE_DATA,
  RESULT_HAS_CONTRIBUTION,
} from '../../helpers/constants'
import {
  getContributionLabels,
  compareArrayProperty,
  compareIdentifier,
  compareSingleProperty,
} from '../../helpers/helpers'
import _ from 'lodash'

const DesktopCompare = (props) => {
  // handler
  const {
    getSearchTerm,
    getCareSearchTerm,
    removeItemFromBookmarks,
    getObjectById,
    handleSelectChange,
    performCompareWithoutSearchTerm,
    getBackPath,
    openAccordionKey,
    isLoading,
  } = props

  const { header: headerLoading, details: detailsLoading } = isLoading

  // props
  const {
    selectedType,
    searchTerm,
    selectOptions,
    compareWithOutSearchTerm,
    renderResults,
    setOpenAccordionKey,
    disableToggleBookmark,
  } = props

  const textContext = useContext(TextContext)
  const state = useSelector((state) => state)
  const isMobile = useMobileSwitch()
  const { compare: CompareState, apiError } = state
  const DISCLAIMER_ANCHOR_ID = 'maintainable-text-disclaimer'
  const gridSizeSM = 12 / CompareState.activeElements.activeIds.length
  const { detailsKeyArray, generalTooltips: CompareTooltips } =
    CompareState.types[selectedType]

  const getLoader = () => (
    <Loader variant='loading'>{textContext['general.loadingData']}</Loader>
  )

  const getMaintainableDisclaimer = (result, id) => {
    const { maintainableTextDisclaimer } = result
    if (!maintainableTextDisclaimer) return null
    return maintainableTextDisclaimer.indexOf(id) > -1
      ? DISCLAIMER_ANCHOR_ID
      : null
  }

  const getNoDetailData = () => (
    <div className='no-detail-data'>
      <p>{textContext['compare.dataRow.noDetailsData']}</p>
    </div>
  )

  // get the other compare results except the current rendered one
  const getComparingResultObjects = (excludeID) =>
    CompareState.types[selectedType].results.filter(
      (res) =>
        CompareState.activeElements.activeIds.includes(res.id) &&
        res.id !== excludeID
    )

  // get all contribution rows
  const getRatedQsrDataRowByIdentifier = (identifier) =>
    CompareState.activeElements.activeIds.map((resultID, index) => {
      const result = getObjectById(resultID)
      const currentQsrValue = result.qsr.filter(
        (qsrObject) => qsrObject.identifier === identifier.identifier
      )
      const currentQstValueIsBest =
        currentQsrValue.length !== 0 &&
        compareIdentifier(
          identifier.identifier,
          currentQsrValue[0].rating,
          getComparingResultObjects(result.id)
        )

      if (currentQsrValue.length === 0) {
        return (
          <Grid
            item
            xs={ 12 }
            sm={ gridSizeSM }
            className={ 'detail-item' }
            key={ `qsr-${ identifier }-${ index }` }>
            <NoCompareValue
              compareKey={ getReplacedString({
                search: textContext['noQsrResult.placeholder'],
                replaceWith: identifier.title,
              }) }
              qsr={ true }
              hint={ CompareTooltips.qsr }
            />
          </Grid>
        )
      }

      return (
        <Grid
          item
          xs={ 12 }
          sm={ gridSizeSM }
          className={ 'detail-item' }
          key={ `qsr-${ identifier }-${ index }` }>
          <ComparedDataRow
            key={ `qsr-${ index }` }
            bestValue={ currentQstValueIsBest }
            title={ textContext['quality.headline.qsr'] }
            hint={ CompareTooltips.qsr }
            qsrIndicator={
              <QsrIndicator
                size={ 22 }
                className='qsr-indicator'
                currentLevel={ currentQsrValue[0].rating }
                maxLevel={ 3 }
              />
            }>
            <div className='row-item'>
              <p>{currentQsrValue[0].text.replace('.', '')}</p>
              {currentQstValueIsBest && (
                <div className={ 'checkmark-icon' }>
                  <Icon className='icon-f-checkmark' />
                </div>
              )}
            </div>
          </ComparedDataRow>
        </Grid>
      )
    })

  // count the number con contribution in active results
  const getNumberOfContributionItems = (getCareLevelKeys = false) => {
    let numberOfContributionItems = 0
    let careLevels = []

    CompareState.activeElements.activeIds.forEach((resultID) => {
      const result = getObjectById(resultID)

      const diff = _.difference(
        result.contribution.map((contr) => contr.careLevel),
        careLevels
      )

      numberOfContributionItems += diff.length
      careLevels = [...careLevels, ...diff]
    })

    careLevels.sort()
    // return careLevels
    return !!getCareLevelKeys ? careLevels : numberOfContributionItems
  }

  // get rated contributions
  const getRatedContribution = (contributionIndex, careLvl) =>
    CompareState.activeElements.activeIds.map((resultID, index) => {
      const result = getObjectById(resultID)
      let contribution

      // compare same care level
      // TODO FOUND BETTER WAY TO SOLVE THIS
      if (entryPointId.CAREHOMES === selectedType) {
        contribution = result.contribution.find(
          (contrib) => contrib.careLevel === careLvl
        )
        if (typeof contribution === 'undefined') {
          return (
            <Grid
              item
              xs={ 12 }
              sm={ gridSizeSM }
              className={ 'detail-item' }
              key={ `contribution-${ careLvl }-${ index }` }>
              <NoCompareValue compareKey={ `Pflegestufe ${ careLvl },` } />
            </Grid>
          )
        }
      } else {
        contribution = result.contribution[contributionIndex]
      }

      const { careLevelText, dataCy, hintTitle } = getContributionLabels({
        contribution,
        resultID,
        selectedType,
      })

      return (
        <Grid
          item
          xs={ 12 }
          sm={ gridSizeSM }
          className={ 'detail-item' }
          key={ `contribution-${ index }` }
          data-cy={ dataCy }>
          {(contribution.type === 'personalContribution' ||
            contribution.type === 'contributionDay' ||
            contribution.type === 'contributionNight') && (
            <ComparedDataRow
              bestValue={ compareArrayProperty(
                {
                  curKey: 'contribution',
                  curVal: contribution.amount,
                  curIndex: contributionIndex,
                },
                getComparingResultObjects(resultID)
              ) }
              title={ hintTitle }
              hint={ contribution?.tooltip ? contribution?.tooltip : '' }>
              <p>
                <strong>{contribution.amount}</strong> {careLevelText}
              </p>
            </ComparedDataRow>
          )}

          {contribution.type === 'personalContributionShortTerm' && (
            <ComparedDataRow
              key={ `personalContributionShortTerm-${ index }` }
              bestValue={ compareArrayProperty(
                {
                  curKey: 'personalContributionShortTerm',
                  curVal: contribution.amount,
                  curIndex: contributionIndex,
                  careLevel: contribution.careLevel,
                },
                getComparingResultObjects(resultID)
              ) }
              withOutTooltip={ true }>
              <p>{careLevelText}</p>
            </ComparedDataRow>
          )}
        </Grid>
      )
    })

  const getRatedTransparencyReport = (resultID) => {
    const result = getObjectById(resultID)

    return result.transparencyReport === null ? (
      <NoCompareValue
        compareKey={ `${ textContext['compare.dataRow.noTransparencyReport'] }` }
      />
    ) : (
      <ComparedDataRow
        bestValue={ compareSingleProperty(
          'transparencyReport',
          result.transparencyReport,
          getComparingResultObjects(result.id),
          true
        ) }
        title={ textContext['generalInformation.transparencyReport'] }
        hint={
          CompareTooltips?.transparencyReport
            ? CompareTooltips.transparencyReport
            : ''
        }>
        <p>
          <strong>{result.transparencyReport}</strong>{' '}
          {textContext['accordionRow.transparencyReport.label']}
        </p>
      </ComparedDataRow>
    )
  }

  const getSelectHeaderLabel = (index) => {
    let headerLabel
    switch (index) {
      case 0:
        headerLabel = 'compare.select.labelFirst'
        break
      case 1:
        headerLabel = 'compare.select.labelSecond'
        break
      default:
        headerLabel = 'compare.select.labelThird'
    }
    return textContext[headerLabel]
  }

  return (
    <Styled.DesktopCompareView>
      {entryPointId.HOSPITALS === selectedType && !isMobile && (
        <MessageBox className='compare-hint-box' data-cy='compare-hint-box'>
          {CompareState.types[entryPointId.HOSPITALS].isTreatmentReason &&
          !compareWithOutSearchTerm ? (
            <p>
              {textContext['compare.searchHint.hospital']}
              <span className='searchTerm'>»{getSearchTerm()}«</span>.
              {searchTerm !== '' && (
                <>
                  {textContext['compare.link.preRemoveSearchTerm']}
                  <Link
                    data-cy='btn-compare-without-treatment'
                    onClick={ (e) => performCompareWithoutSearchTerm(e) }>
                    {textContext['compare.link.removeSearchTerm']}
                  </Link>
                </>
              )}
            </p>
          ) : (
            <p>{textContext['compare.searchHint.withOutTreatmentReason']}</p>
          )}
        </MessageBox>
      )}
      {entryPointId.CAREHOMES === selectedType && !isMobile && (
        <MessageBox className='compare-hint-box' data-cy='compare-hint-box'>
          {CompareState.types[entryPointId.CAREHOMES] && (
            <p>
              {textContext['compare.searchHint.carehomes']}
              {getCareSearchTerm()}.
            </p>
          )}
        </MessageBox>
      )}
      {entryPointId.CARESERVICES === selectedType && !isMobile && (
        <MessageBox className='compare-hint-box' data-cy='compare-hint-box'>
          {CompareState.types[entryPointId.CARESERVICES] && (
            <p>
              {textContext['compare.searchHint.careservices']}
              {getCareSearchTerm()}.
            </p>
          )}
        </MessageBox>
      )}
      {entryPointId.DAYANDNIGHTCARE === selectedType && !isMobile && (
        <MessageBox className='compare-hint-box' data-cy='compare-hint-box'>
          {CompareState.types[entryPointId.DAYANDNIGHTCARE] && (
            <p>
              {textContext['compare.searchHint.dayandnightcare']}
              {getCareSearchTerm()}.
            </p>
          )}
        </MessageBox>
      )}

      {headerLoading && (
        <Grid container spacing={ 4 } className='compare-container first loader'>
          {getLoader()}
        </Grid>
      )}

      {/* (desktop) get general information's header */}
      <ErrorBoundary>
        {renderResults && !isMobile && (
          <>
            {/* select and column title */}
            <Grid
              container
              spacing={ 4 }
              className='compare-container first'
              data-cy='compare-select-header'>
              {CompareState.activeElements.activeIds.map((resultID, index) => {
                const result = getObjectById(resultID)
                return (
                  <Grid
                    item
                    xs={ 12 }
                    sm={ gridSizeSM }
                    key={ index }
                    className={ 'compare-item' }>
                    <div className='select-container'>
                      <span className='select-label'>
                        {getSelectHeaderLabel(index)}
                      </span>
                      <Select
                        id={ `compare-select-${ result.id }` }
                        className='compare-select'
                        handleSelect={ (addId) =>
                          handleSelectChange({
                            addId,
                            replaceId: resultID,
                          })
                        }
                        value={ result.id }
                        options={ selectOptions(result.id) }
                        context='resultSort'
                        excludeCurrentValue
                        disabled={ selectOptions(result.id).length === 1 }
                      />
                    </div>
                  </Grid>
                )
              })}
            </Grid>

            {/* general comparison - header */}
            <Grid
              container
              spacing={ 4 }
              className='compare-container'
              data-cy='compare-general-information'>
              {CompareState.activeElements.activeIds.map((resultID, index) => {
                const result = getObjectById(resultID)
                return (
                  <Grid
                    item
                    xs={ 12 }
                    sm={ gridSizeSM }
                    key={ index }
                    className={ 'compare-item' }>
                    <GeneralInformationCompare
                      key={ index }
                      type={ 'header' }
                      data={ result }
                      resultType={ selectedType }
                      onToggleBookmark={ (item) => removeItemFromBookmarks(item) }
                      disableToggleBookmark={ disableToggleBookmark }
                      withOutTreatmentReason={ compareWithOutSearchTerm }
                    />
                  </Grid>
                )
              })}
            </Grid>

            {/* get compare elements */}
            {RESULT_HAS_COMPARE_DATA.includes(selectedType) && (
              <>
                {/* TODO contribution compare for care services currently not possible, brocken data */}
                {RESULT_HAS_CONTRIBUTION.includes(selectedType) &&
                  selectedType !== entryPointId.CARESERVICES && (
                    <>
                      {CompareState.types[selectedType].compareKeys.includes(
                        'contribution'
                      ) &&
                        Array.from(
                          Array(getNumberOfContributionItems()),
                          (_, index) => {
                            const careLevel = getNumberOfContributionItems(true)

                            return (
                              <Grid
                                container
                                spacing={ 4 }
                                className='compare-container'
                                key={ `contribution-${ index }` }
                                data-cy='contribution-container'>
                                {getRatedContribution(index, careLevel[index])}
                              </Grid>
                            )
                          }
                        )}
                    </>
                  )}

                {/* transparenz report (careservices && carehomes) */}
                {CompareState.types[selectedType].compareKeys.includes(
                  'transparencyReport'
                ) && (
                  <Grid
                    container
                    spacing={ 4 }
                    className='compare-container'
                    data-cy='transparencyReport-container'>
                    {CompareState.activeElements.activeIds.map(
                      (resultID, index) => (
                        <Grid
                          item
                          xs={ 12 }
                          sm={ gridSizeSM }
                          className={ 'detail-item' }
                          key={ `transparencyReport-${ index }` }>
                          {getRatedTransparencyReport(resultID)}
                        </Grid>
                      )
                    )}
                  </Grid>
                )}

                {/* pricePerHour (careservices) */}
                {CompareState.types[selectedType].compareKeys.includes(
                  'pricePerHour'
                ) && (
                  <Grid
                    container
                    spacing={ 4 }
                    className='compare-container'
                    data-cy='pricePerHour-container'>
                    {CompareState.activeElements.activeIds.map(
                      (resultID, index) => {
                        const result = getObjectById(resultID)

                        return (
                          <Grid
                            item
                            xs={ 12 }
                            sm={ gridSizeSM }
                            className={ 'detail-item' }
                            key={ `treatment-${ index }` }>
                            <ComparedDataRow
                              bestValue={ compareSingleProperty(
                                'pricePerHour',
                                result.pricePerHour,
                                getComparingResultObjects(result.id),
                                true
                              ) }
                              withOutTooltip={ true }
                              className='first-paragraph-margin'>
                              <>
                                <p>
                                  {
                                    textContext[
                                      'generalInformation.pricePerHour'
                                    ]
                                  }
                                </p>
                                <p>
                                  <strong>{result.pricePerHour}</strong>
                                </p>
                              </>
                            </ComparedDataRow>
                          </Grid>
                        )
                      }
                    )}
                  </Grid>
                )}

                {/* services (careservices) */}
                {CompareState.types[selectedType].compareKeys.includes(
                  'services'
                ) && (
                  <Grid
                    container
                    spacing={ 4 }
                    className='compare-container'
                    data-cy='services-container'>
                    {CompareState.activeElements.activeIds.map(
                      (resultID, index) => {
                        const result = getObjectById(resultID)

                        return (
                          <Grid
                            item
                            xs={ 12 }
                            sm={ gridSizeSM }
                            className={ 'detail-item' }
                            key={ `services-${ index }` }>
                            <ComparedDataRow
                              bestValue={ compareArrayProperty(
                                {
                                  curKey: 'services',
                                  curVal: result.services.length,
                                  curIndex: false,
                                },
                                getComparingResultObjects(result.id),
                                true
                              ) }
                              withOutTooltip={ true }
                              className='first-paragraph-margin'>
                              <>
                                <p>
                                  {textContext['generalInformation.services']}
                                </p>
                                <ul className='service-list'>
                                  {result.services.map((service, index) => (
                                    <li key={ index }>
                                      {service.title}
                                      {index + 1 !== result.services.length
                                        ? ','
                                        : ''}
                                    </li>
                                  ))}
                                </ul>
                              </>
                            </ComparedDataRow>
                          </Grid>
                        )
                      }
                    )}
                  </Grid>
                )}

                {/* hospital specific compare data */}
                {selectedType === entryPointId.HOSPITALS && (
                  <>
                    {/* treatments */}
                    {CompareState.types[selectedType].compareKeys.includes(
                      'treatment'
                    ) && (
                      <Grid
                        container
                        spacing={ 4 }
                        className='compare-container'
                        data-cy='treatment-container'>
                        {CompareState.activeElements.activeIds.map(
                          (resultID, index) => {
                            const result = getObjectById(resultID)
                            return (
                              <Grid
                                item
                                xs={ 12 }
                                sm={ gridSizeSM }
                                className={ 'detail-item' }
                                key={ `treatment-${ index }` }>
                                <ComparedDataRow
                                  bestValue={ compareSingleProperty(
                                    'treatmentCases',
                                    result.treatment.cases,
                                    getComparingResultObjects(result.id)
                                  ) }
                                  title={ `${
                                    CompareState.types[selectedType].isApo
                                      ? textContext[
                                          'compare.generalInformation.labelTreatment.stationary'
                                        ]
                                      : textContext[
                                          'compare.generalInformation.labelTreatment'
                                        ]
                                  }: ` }
                                  hint={
                                    CompareState.types[selectedType].isApo
                                      ? CompareTooltips.treatmentCasesAmbulant
                                      : CompareTooltips.treatmentCases
                                  }>
                                  <p>
                                    <strong>{result.treatment.cases}</strong> (
                                    {
                                      mappedTreatmentScores[
                                        result.treatment.scoring
                                      ]
                                    }
                                    )
                                  </p>
                                </ComparedDataRow>
                              </Grid>
                            )
                          }
                        )}
                      </Grid>
                    )}

                    {/* qsr data */}
                    {Array.isArray(
                      CompareState.types[selectedType].qsrIdentifier
                    ) &&
                      CompareState.types[selectedType].qsrIdentifier.length >
                        0 &&
                      CompareState.types[selectedType].qsrIdentifier.map(
                        (identifier, index) => (
                          <Grid
                            container
                            spacing={ 4 }
                            className='compare-container'
                            key={ `qsr-identifier-${ index }` }
                            data-cy='qsr-container'>
                            {getRatedQsrDataRowByIdentifier(identifier)}
                          </Grid>
                        )
                      )}

                    {/* iqtig */}
                    {CompareState.types[selectedType].compareKeys.includes(
                      'iqtig'
                    ) && (
                      <Grid
                        container
                        spacing={ 4 }
                        className='compare-container'
                        data-cy='iqtig-container'>
                        {CompareState.activeElements.activeIds.map(
                          (resultID, index) => {
                            const result = getObjectById(resultID)

                            return result.iqtig ? (
                              <Grid
                                item
                                xs={ 12 }
                                sm={ gridSizeSM }
                                className={ 'detail-item' }
                                key={ `iqtig-${ index }` }>
                                <ComparedDataRow
                                  bestValue={ compareSingleProperty(
                                    'iqtig',
                                    result.iqtig,
                                    getComparingResultObjects(result.id)
                                  ) }
                                  title={ `${ textContext['quality.headline.iqtig'] }:` }
                                  hint={ CompareTooltips.iqtig }>
                                  {result.iqtig}%{' '}
                                  {
                                    textContext[
                                      'compare.generalInformation.iqtigIndicators'
                                    ]
                                  }
                                </ComparedDataRow>
                              </Grid>
                            ) : (
                              <Grid
                                item
                                xs={ 12 }
                                sm={ gridSizeSM }
                                className={ 'detail-item' }
                                key={ `iqtig-${ index }` }>
                                <NoCompareValue
                                  compareKey={ `${ textContext['quality.headline.iqtig'] }:` }
                                />
                              </Grid>
                            )
                          }
                        )}
                      </Grid>
                    )}

                    {/* recommendation */}
                    {CompareState.types[selectedType].compareKeys.includes(
                      'general'
                    ) && (
                      <Grid
                        container
                        spacing={ 4 }
                        className='compare-container'
                        data-cy='recommendation-container'>
                        {CompareState.activeElements.activeIds.map(
                          (resultID, index) => {
                            const result = getObjectById(resultID)
                            return result.peq.general !== null ? (
                              <Grid
                                item
                                xs={ 12 }
                                sm={ gridSizeSM }
                                className={ 'detail-item' }
                                key={ `recommendation-${ index }` }>
                                <ComparedDataRow
                                  key='recommendation'
                                  bestValue={ compareSingleProperty(
                                    'general',
                                    result.peq.general,
                                    getComparingResultObjects(result.id)
                                  ) }
                                  title={ `${ textContext['searchHospital.tooltip.title.recommendation'] }:` }
                                  hint={ CompareTooltips.recommendation }>
                                  <BarGraph
                                    percent={ result.peq.general }
                                    className='bar-graph'
                                  />
                                </ComparedDataRow>
                              </Grid>
                            ) : (
                              <Grid
                                item
                                xs={ 12 }
                                sm={ gridSizeSM }
                                className={ 'detail-item' }
                                key={ `recommendation-${ index }` }>
                                <NoCompareValue
                                  key='recommendation'
                                  compareKey={
                                    textContext[
                                      'searchHospital.tooltip.title.recommendation'
                                    ]
                                  }
                                />
                              </Grid>
                            )
                          }
                        )}
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}

            {/* general comparison - address */}
            <Grid container spacing={ 4 } className='compare-container'>
              {CompareState.activeElements.activeIds.map((resultID, index) => {
                const result = getObjectById(resultID)

                return (
                  <Grid
                    item
                    xs={ 12 }
                    sm={ gridSizeSM }
                    key={ index }
                    className={ 'compare-item' }>
                    <GeneralInformationCompare
                      key={ index }
                      type={ 'contact' }
                      data={ result }
                      resultType={ selectedType }
                      onToggleBookmark={ (item) => removeItemFromBookmarks(item) }
                    />
                  </Grid>
                )
              })}
            </Grid>

            {/* all details currently loading */}
            {/* { (isLoading.details)*/}
            {detailsLoading ? (
              <Grid container spacing={ 4 } className='compare-container'>
                {CompareState.activeElements.activeIds.map((item, index) => (
                  <Grid
                    item
                    xs={ 12 }
                    sm={ gridSizeSM }
                    key={ index }
                    className={ 'detail-item' }>
                    {getLoader()}
                  </Grid>
                ))}
              </Grid>
            ) : (
              detailsKeyArray.map((detailKeys, index) => (
                <Grid
                  container
                  spacing={ 4 }
                  className='compare-container'
                  key={ index }>
                  {
                    // map over all active element
                    CompareState.activeElements.activeIds.map(
                      (resultID, index) => {
                        const result = getObjectById(resultID)

                        if (!result?.details) {
                          return false
                        }

                        const itemData = result.details[detailKeys]
                        const label =
                          (ACCORDION_TITLES[selectedType] &&
                            ACCORDION_TITLES[selectedType].hasOwnProperty(
                              detailKeys
                            ) &&
                            textContext[
                              `${ ACCORDION_TITLES[selectedType][detailKeys] }.title`
                            ]) ||
                          textContext[`${ detailKeys }.title`] ||
                          ''

                        return (
                          <Grid
                            item
                            xs={ 12 }
                            sm={ gridSizeSM }
                            key={ index }
                            className={ 'detail-item' }>
                            <MobileSwitchProvider
                              forceMobile={ true }
                              key={ index }>
                              <Accordion
                                label={ label }
                                tooltip={
                                  itemData &&
                                  itemData.hasOwnProperty('itemData')
                                    ? itemData.itemData
                                    : false
                                }
                                disclaimerAnchor={ getMaintainableDisclaimer(
                                  result,
                                  detailKeys
                                ) }
                                open={ openAccordionKey === detailKeys }
                                triggerStateChangeToParent={ () =>
                                  setOpenAccordionKey(detailKeys)
                                }
                                className='compare-accordion'>
                                {itemData ? (
                                  <DetailContentFactory
                                    detailsKey={ detailKeys }
                                    entryPoint={ selectedType }
                                    resultData={ result.details }
                                    itemData={ itemData }
                                    isTreatmentReason={ false }
                                    backLink={ () => getBackPath() }
                                  />
                                ) : (
                                  getNoDetailData()
                                )}
                              </Accordion>
                            </MobileSwitchProvider>
                          </Grid>
                        )
                      }
                    )
                  }
                </Grid>
              ))
            )}
          </>
        )}
      </ErrorBoundary>

      {/* show api error */}
      {apiError && <MessageBox isError={ true } />}
    </Styled.DesktopCompareView>
  )
}

DesktopCompare.propTypes = {
  getSearchTerm: func.isRequired,
  getCareSearchTerm: func.isRequired,
  removeItemFromBookmarks: func.isRequired,
  getObjectById: func.isRequired,
  getBackPath: func.isRequired,
  handleSelectChange: func.isRequired,
  performCompareWithoutSearchTerm: func.isRequired,
  selectOptions: func.isRequired,
  selectedType: string.isRequired,
  searchTerm: string,
  compareWithOutSearchTerm: bool.isRequired,
  renderResults: bool.isRequired,
  openAccordionKey: string.isRequired,
  setOpenAccordionKey: func.isRequired,
  isLoading: shape({
    header: bool,
    details: bool,
  }).isRequired,
  disableToggleBookmark: bool.isRequired,
}

export default DesktopCompare
